/**
 * Catálogo de minijuegos. Datos, no código: agregar uno es una entrada más, igual
 * que con las salas.
 *
 * Los cinco límites de cada juego no son adorno — cada uno tapa un ataque
 * distinto, y quitar cualquiera abre un agujero:
 *
 *   maxScore            un puntaje absurdo
 *   maxPointsPerSecond  un puntaje real logrado demasiado rápido
 *   maxSeconds          reenviar una sesión vieja que quedó abierta
 *   coinCap             farmear con paciencia dentro de los límites
 *   coinsPerPoint       la conversión, que vive en el servidor y no en el cliente
 */
import { DURATION_SECONDS, MAX_POINTS_PER_FRUIT, maxFruits } from "./frozen.js";
export const GAME_IDS = ["demo", "aja", "frozen"];
export const GAMES = {
    /**
     * JUEGO DE RELLENO. Existe para cerrar el círculo ganar → gastar y para que los
     * tests puedan ejercitarlo. Reemplázalo por minijuegos reales; la economía no
     * cambia cuando lo hagas.
     */
    demo: {
        id: "demo",
        name: "Juego de prueba",
        coinsPerPoint: 1,
        maxScore: 100,
        maxPointsPerSecond: 5,
        maxSeconds: 600,
        coinCap: 200,
        coinCapWindowMs: 60 * 60 * 1000,
    },
    /**
     * El experimento del Parque ¡Ajá!: parar el péndulo dentro de la franja.
     *
     * Los límites salen del juego, no al revés. Un acierto exige que el marcador
     * recorra la pista, y eso no baja de medio segundo, así que 2 puntos por segundo es
     * el techo real con margen: nadie legítimo lo roza y un cliente que dispare aciertos
     * seguidos lo cruza en el primer intento. `maxScore` es la franja mínima — más allá
     * de 25 aciertos la franja ya no cabe en la pista, así que un puntaje mayor no lo
     * produce el juego.
     */
    aja: {
        id: "aja",
        name: "Experimento ¡Ajá!",
        coinsPerPoint: 2,
        maxScore: 25,
        maxPointsPerSecond: 2,
        maxSeconds: 300,
        coinCap: 200,
        coinCapWindowMs: 60 * 60 * 1000,
    },
    /**
     * El carrito de la FIEC Vieja: romper fruta cayéndole encima antes de que se acabe
     * el tiempo. Las reglas y las medidas están en `frozen.ts`.
     *
     * Es el primer juego cuyos topes **se derivan** en vez de escribirse. El motivo es
     * que acá se pueden derivar de verdad: el plan de aparición suelta la misma cantidad
     * de frutas en toda partida —los tiempos salen de la rampa, no de la semilla— así que
     * "lo máximo que se puede sacar" es una multiplicación exacta y no una estimación.
     * Escribirla a mano significaría reajustarla cada vez que se toque la cadencia, y la
     * vez que se olvide, el servidor le rechaza la partida a quien juega bien.
     */
    frozen: {
        id: "frozen",
        name: "Frozen de Frutas",
        /*
         * Media moneda por punto. Una partida decente ronda los 70 puntos (35 monedas) y
         * uno que no deje escapar NADA, con cadenas de dos, ronda los 180 (90). Está a
         * propósito por encima del ¡Ajá! —que tope da 50— porque esta partida dura el doble
         * y pide las dos manos.
         */
        coinsPerPoint: 0.5,
        /*
         * El techo absoluto: que no se escapara ninguna, que todas fueran maracuyá y que cada
         * pisada entrara con la cadena al máximo. No lo produce ninguna partida real —ni siquiera
         * una perfecta, porque el surtido sortea— pero es una cota DURA, que es justo lo
         * que `maxScore` tiene que ser.
         */
        maxScore: maxFruits() * MAX_POINTS_PER_FRUIT,
        /*
         * El ritmo se deriva del techo, y sale exacto porque la partida dura siempre lo
         * mismo: el ají aturde pero no roba tiempo, justo para que este cociente siga
         * significando algo.
         *
         * Una partida legítima tarda `DURATION_SECONDS` o MÁS —arranca antes de que corra
         * el reloj y cierra después del último segundo— así que su cociente nunca pasa de
         * este valor. El 5% es para el desfase entre el reloj del navegador y el del
         * servidor, no para dar margen a nadie.
         *
         * Contra un cliente modificado esto NO alcanza, y no pretende: quien mande el techo
         * a los 45 segundos pasa. Lo que lo acota es `coinCap`, y es el reparto de siempre —
         * acá se rechaza lo imposible, allá se limita lo posible.
         */
        maxPointsPerSecond: ((maxFruits() * MAX_POINTS_PER_FRUIT) / DURATION_SECONDS) * 1.05,
        /* Holgado contra `DURATION_SECONDS`: una pestaña de fondo estrangula los temporizadores
           del navegador y una partida real puede tardar bastante más de lo que dura. */
        maxSeconds: 180,
        coinCap: 200,
        coinCapWindowMs: 60 * 60 * 1000,
    },
};
export const GAME_LIST = GAME_IDS.map((id) => GAMES[id]);
export function isGameId(value) {
    return typeof value === "string" && GAME_IDS.includes(value);
}
export function getGame(id) {
    return isGameId(id) ? GAMES[id] : undefined;
}
//# sourceMappingURL=games.js.map